import { fromJS } from 'immutable';
import { createSelector } from 'reselect';

import { status } from '@src/global/status';
import { calculateCollateralCostCents } from '@src/utils/collateralPricingHelpers';
import * as utils from '@src/utils/duckHelpers';

const cartItemsTypes = {
    ...utils.requestTypes('CART_ITEMS'),
    CART_ITEMS_CREATE: 'CART_ITEMS_CREATE',
    CART_ITEMS_CREATE_SUCCESS: 'CART_ITEMS_CREATE_SUCCESS',
    CART_ITEMS_CREATE_FAILURE: 'CART_ITEMS_CREATE_FAILURE',
    CART_ITEMS_REMOVE: 'CART_ITEMS_REMOVE',
    CART_ITEMS_REMOVE_SUCCESS: 'CART_ITEMS_REMOVE_SUCCESS',
    CART_ITEMS_REMOVE_FAILURE: 'CART_ITEMS_REMOVE_FAILURE',
    CART_ITEMS_CHECKOUT: 'CART_ITEMS_CHECKOUT',
    CART_MODAL_SHOW: 'CART_MODAL_SHOW',
    CART_MODAL_HIDE: 'CART_MODAL_HIDE',
};

const cartItemsActions = {
    request: (params) => utils.action(cartItemsTypes.CART_ITEMS_REQUEST, { payload: params }),
    success: (response) => utils.action(cartItemsTypes.CART_ITEMS_SUCCESS, { payload: response }),
    failure: (error) => utils.action(cartItemsTypes.CART_ITEMS_FAILURE, { error }),
    cartItemsSet: (obj) => utils.action(cartItemsTypes.CART_ITEMS_SET, { payload: obj }),
    create: (data) => utils.action(cartItemsTypes.CART_ITEMS_CREATE, { payload: data }),
    createSuccess: (response) => utils.action(cartItemsTypes.CART_ITEMS_CREATE_SUCCESS, { payload: response }),
    createFailure: (error) => utils.action(cartItemsTypes.CART_ITEMS_CREATE_FAILURE, { error }),
    remove: (id) => utils.action(cartItemsTypes.CART_ITEMS_REMOVE, { payload: { id } }),
    removeSuccess: (response) => utils.action(cartItemsTypes.CART_ITEMS_REMOVE_SUCCESS, { payload: response }),
    removeFailure: (error) => utils.action(cartItemsTypes.CART_ITEMS_REMOVE_FAILURE, { error }),
    checkout: (params) => utils.action(cartItemsTypes.CART_ITEMS_CHECKOUT, { payload: params }),
    showCartModal: () => utils.action(cartItemsTypes.CART_MODAL_SHOW),
    hideCartModal: () => utils.action(cartItemsTypes.CART_MODAL_HIDE),
};

const initialState = fromJS({
    loaded: status.LOADING,
    cartItems: [],
    page: 0,
    total: 0,
    error: null,
    isCartModalShown: false,
});

const cartItems = (state = initialState, action) => {
    switch (action.type) {
    case cartItemsTypes.CART_ITEMS_REQUEST:
        return state.merge({
            loaded: status.LOADING,
            error: null,
        });
    case cartItemsTypes.CART_ITEMS_SUCCESS:
        if (!action.payload) return state;

        return state.merge({
            loaded: status.COMPLETE,
            cartItems: fromJS(action.payload.cart_items || []),
            page: action.payload.page,
            total: action.payload.total,
            error: null,
        });
    case cartItemsTypes.CART_ITEMS_SET:
        if (action.payload && action.payload.cart_items) {
            return state.merge({
                cartItems: fromJS(action.payload.cart_items),
                total: action.payload.total,
                loaded: status.COMPLETE,
            });
        }

        return state.merge({
            cartItems: fromJS([]),
            loaded: status.EMPTY,
        });
    case cartItemsTypes.CART_ITEMS_FAILURE:
        return state.merge({
            loaded: status.FAILED,
            cartItems: [],
            page: 0,
            total: 0,
            error: action.error,
        });
    case cartItemsTypes.CART_MODAL_SHOW:
        return state.set('isCartModalShown', true);
    case cartItemsTypes.CART_MODAL_HIDE:
        return state.set('isCartModalShown', false);
    default:
        return state;
    }
};

const getCartItems = (state) => state.get('app').get('cartItems');
const getItemsList = createSelector([getCartItems], (cartItems) => cartItems.get('cartItems'));

const selectors = {
    getCartItems: createSelector([getCartItems], (cartItems) => cartItems),
    getItemsList,
    getShowCartModal: createSelector([getCartItems], (cartItems) => cartItems.get('isCartModalShown')),
    getCartTotalCents: createSelector([getItemsList], (items) => {
        return items.reduce((sum, item) => {
            const itemJS = item.toJS();
            return sum + calculateCollateralCostCents(itemJS.resource?.pricing_info, itemJS.data?.quantity);
        }, 0);
    }),
};

export {
    cartItems as default,
    cartItemsActions,
    cartItemsTypes,
    selectors,
};
